import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { FresnelParameters } from '@babylonjs/core/Materials/fresnelParameters';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Scene } from '@babylonjs/core/scene';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';

import type { NodeDto, NodeTypeDto } from '../generated/contracts';
import { cloneModelTemplate, ModelLibrary, type ModelLoadSummary } from './ModelLibrary';
import { buildSilhouette, restingAlphaFor } from './Silhouettes';

const DEFAULT_SHAPE_COLOR = '#4C8DFF';

/**
 * A view-dependent rim on every node, done entirely inside the material — no post-process, no
 * extra draw call. `StandardMaterial.emissiveFresnelParameters` multiplies `emissiveColor` by a
 * term that is ~1 at a surface's silhouette edge (grazing view angle) and ~0 facing the camera
 * dead-on (Babylon's `default.fragment.js`, `EMISSIVEFRESNEL`; confirmed against the shader
 * source and `FresnelParameters`' own doc comment: `leftColor` is the grazing/edge weight,
 * `rightColor` the facing/centre one — opposite of what the names alone suggest). `leftColor`
 * white / `rightColor` black here means the fresnel term is purely an intensity envelope, not a
 * colour of its own — the rim's actual colour and brightness both come from `emissiveColor`,
 * which is what lets `setHeldLook` below reuse this same envelope for free: holding a node
 * overwrites `emissiveColor` with the holder's own tint, so a held node's rim brightens toward
 * its edges in the holder's colour instead of competing with the alpha fade for attention.
 * `RIM_EMISSIVE_COLOR` is deliberately dim — "enough to separate a shape from the wall behind
 * it, not a neon outline" (the brief's own words) — controlled by keeping the emissive colour
 * low rather than by widening the fresnel falloff, so the rim's *shape* (bias/power) stays the
 * same whether resting or held.
 */
const RIM_FRESNEL_BIAS = 0.15;
const RIM_FRESNEL_POWER = 3;
const RIM_EMISSIVE_COLOR = new Color3(0.16, 0.17, 0.2);

/**
 * A second, independent Fresnel — on `diffuseColor` rather than `emissiveColor` — that darkens a
 * shape's own *lit* contribution toward its silhouette edge (`StandardMaterial
 * .diffuseFresnelParameters`, `DIFFUSEFRESNEL`). Multiplies `diffuseBase`, the accumulated
 * per-pixel lighting term, not the material's base colour, so it reads as a subtle edge shadow —
 * the cheapest available stand-in for real ambient occlusion: no new geometry, no second pass,
 * one more per-pixel multiply on lighting the GPU was already computing. `EDGE_DARKEN_AMOUNT` is
 * how much of the lit contribution survives at the very edge; 1.0 would be no darkening at all.
 */
const EDGE_DARKEN_FRESNEL_BIAS = 0.2;
const EDGE_DARKEN_FRESNEL_POWER = 2.5;
const EDGE_DARKEN_AMOUNT = 0.6;

/**
 * Specular highlight shape for every node material — a small, fairly tight highlight is what
 * makes a shape read as a solid manufactured object rather than a flat, untextured primitive.
 * Deliberately not what the room shell's own materials use (`SceneRoot.material` zeroes specular
 * entirely, because a highlight sweeping across a whole flat wall as the head moves reads as a
 * smear) — a node is small enough that its highlight reads as one stable point instead.
 */
const NODE_SPECULAR_COLOR = new Color3(0.35, 0.35, 0.38);
const NODE_SPECULAR_POWER = 48;

/**
 * How solid a node stays while somebody is holding it.
 *
 * Fading rather than outlining. An outline was tried first and read badly in a headset: the
 * edges renderer draws a hard line at every crease, so a box looked like a wireframe overlay
 * rather than a highlighted object, and at a distance the line thinned to nothing. Ghosting
 * the whole shape reads instantly and, usefully, lets you see what is behind the thing being
 * dragged across your view.
 */
const HELD_ALPHA = 0.42;

/** How strongly the holder's colour is mixed into the fade. Enough to identify, not recolour. */
const HELD_TINT = 0.5;

/** What a node's material looked like before anyone picked it up. */
interface NodeMetadata {
  nodeId: string;
  kind: number;
  baseAlpha: number;
  baseEmissive: Color3;
}

/**
 * Turns a NodeDto into a mesh, and keeps it in step with later versions of that node.
 *
 * What a shape *looks* like comes from the catalogue fetched at boot: the DTO says `database`,
 * the catalogue says that means the `drum` silhouette in green, and `Silhouettes.ts` builds it.
 * An unknown key still renders — as a box — so a client older than the catalogue degrades
 * rather than failing to draw the room.
 */
export class NodeMeshFactory {
  /** Catalogue by key, replaced wholesale when it arrives. Empty until then. */
  private catalog = new Map<string, NodeTypeDto>();

  /** Preloaded .glb geometry for whichever catalogue types have a model behind them. */
  private readonly models: ModelLibrary;

  constructor(
    private readonly scene: Scene,
    private readonly parent: TransformNode,
    // Defaulted rather than always constructed here so a test can supply one it has already
    // populated via ModelLibrary.loadFromBytes — see NodeMeshFactory.test.ts. Every real caller
    // (just RoomView) takes the default.
    models: ModelLibrary = new ModelLibrary(scene),
  ) {
    this.models = models;
  }

  /** Supplied once at boot, before any node is built. */
  setCatalog(types: NodeTypeDto[]): void {
    this.catalog = new Map(types.map((type) => [type.key, type]));
  }

  /**
   * Preloads every model-backed catalogue type's .glb, once, at boot. Call after setCatalog and
   * before the first create() — create() is synchronous and cannot itself await a load, so
   * whatever has not finished loading by the time a node is first built falls back to that
   * type's procedural silhouette for good (see ModelLibrary and CLAUDE.md, "The splash screen
   * exists so entering VR is instant").
   */
  async preloadModels(types: NodeTypeDto[]): Promise<void> {
    await this.models.preloadAll(types);
  }

  /**
   * A model-backed type's preloaded template, if one exists — the same lookup createShape uses
   * below, exposed so a caller that clones its own preview (PaletteUI's tray minis) can share
   * the identical template rather than the tray inventing a second way to reach it.
   */
  modelTemplateFor(key: string): Mesh | undefined {
    return this.models.templateFor(key);
  }

  /** The model library's own boot-time load outcome, for SessionScreen — see
   *  ModelLibrary.summary. */
  modelSummary(): ModelLoadSummary {
    return this.models.summary();
  }

  /** The catalogue entry for a node, if the key is one we know. */
  typeOf(node: NodeDto): NodeTypeDto | undefined {
    return node.shapeType === null || node.shapeType === undefined
      ? undefined
      : this.catalog.get(node.shapeType);
  }

  create(node: NodeDto): AbstractMesh {
    const mesh = this.createShape(node);

    mesh.parent = this.parent;

    // The resting look is recorded at construction, because the held look overwrites alpha
    // and emissive and there is otherwise nothing to restore them to.
    const material = mesh.material;

    mesh.metadata = {
      nodeId: node.id,
      kind: node.kind,
      baseAlpha: material?.alpha ?? 1,
      baseEmissive:
        material instanceof StandardMaterial ? material.emissiveColor.clone() : Color3.Black(),
    } satisfies NodeMetadata;

    // Quaternions throughout: the server sends one, and mixing in Euler angles here would
    // reintroduce order-of-rotation questions the wire format deliberately avoids.
    mesh.rotationQuaternion = Quaternion.Identity();

    this.applyTransform(mesh, node);

    return mesh;
  }

  /**
   * Shows, or stops showing, that a node is in somebody's hands.
   *
   * One treatment for both cases on purpose: your own grab and someone else's differ only by
   * whose colour it is. The colour is what says "ask Ada" rather than "try again".
   */
  setHeldLook(mesh: AbstractMesh, holderColor: string | null): void {
    const material = mesh.material;
    const base = mesh.metadata as NodeMetadata | undefined;

    if (!(material instanceof StandardMaterial) || !base) {
      return;
    }

    if (holderColor === null) {
      material.alpha = base.baseAlpha;
      material.emissiveColor.copyFrom(base.baseEmissive);
      return;
    }

    material.alpha = base.baseAlpha * HELD_ALPHA;
    material.emissiveColor.copyFrom(Color3.FromHexString(holderColor).scale(HELD_TINT));
  }

  applyTransform(mesh: AbstractMesh, node: NodeDto): void {
    const { position, rotation, scale } = node.transform;

    mesh.position.set(position.x, position.y, position.z);

    (mesh.rotationQuaternion ??= Quaternion.Identity()).set(
      rotation.x,
      rotation.y,
      rotation.z,
      rotation.w,
    );

    mesh.scaling.setAll(scale);
  }

  /**
   * Repaints content that changed: recolours the existing material and reapplies the transform.
   *
   * Never rebuilds geometry, and — unlike the doc comment this replaces used to promise — never
   * attempts to detect a shape-type change either. There is nothing to detect: `NodeDto.shapeType`
   * is set once, by `CreateNodeRequest`, and `UpdateNodeRequest` (the only other way a node's
   * content changes) carries no `ShapeType` field at all (`Tunet.Contracts/Requests/`) — the
   * server has no path that ever mutates it. So the `node` this method is handed always carries
   * the same `shapeType` the mesh was originally built from in `create()`/`createShape()`, and a
   * rebuild this method could perform would never have anything to do. `restingAlphaFor` and the
   * cached `baseAlpha`/`baseEmissive` in `create()`'s metadata are derived from that same
   * unchanging `shapeType`/silhouette, which is why this method leaves them alone too — they are
   * still correct for the mesh already built. If a future change ever lets a node's shape type
   * change post-creation, this method would need a real rebuild path (dispose the old mesh's
   * geometry, rebuild via `createShape`, re-anchor edges/labels that hold a reference to the old
   * mesh) — plan/open-items.md, "`NodeMeshFactory.refresh` promises a geometry rebuild it does
   * not perform," where this was found.
   */
  refresh(mesh: AbstractMesh, node: NodeDto): AbstractMesh {
    const material = mesh.material;
    if (material instanceof StandardMaterial) {
      material.diffuseColor = Color3.FromHexString(this.colorFor(node));
    }

    this.applyTransform(mesh, node);

    return mesh;
  }

  private createShape(node: NodeDto): Mesh {
    const type = this.typeOf(node);
    const silhouette = type?.silhouette ?? node.shapeType ?? 'box';

    // A model-backed type wins when its .glb preloaded successfully; anything else — no model
    // for this type (most of the catalogue), or one that failed to load — falls back to the
    // catalogue's own silhouette, same as an entirely unrecognised type falls back to a box.
    const template = type && this.models.templateFor(type.key);
    const mesh = template
      ? cloneModelTemplate(template, `node-${node.id}`)
      : buildSilhouette(silhouette, this.scene);

    const material = new StandardMaterial(`node-${node.id}`, this.scene);
    material.diffuseColor = Color3.FromHexString(this.colorFor(node));
    material.specularColor = NODE_SPECULAR_COLOR;
    material.specularPower = NODE_SPECULAR_POWER;

    // Rim (emissive Fresnel) and edge-darkening (diffuse Fresnel) — see both constants' own doc
    // comments above for the mechanism. Two independent FresnelParameters instances, not one
    // shared: emissiveFresnelParameters and diffuseFresnelParameters are different properties on
    // the material and each needs its own bias/power/colours regardless.
    material.emissiveColor = RIM_EMISSIVE_COLOR.clone();
    material.emissiveFresnelParameters = new FresnelParameters({
      bias: RIM_FRESNEL_BIAS,
      power: RIM_FRESNEL_POWER,
      leftColor: Color3.White(),
      rightColor: Color3.Black(),
    });
    material.diffuseFresnelParameters = new FresnelParameters({
      bias: EDGE_DARKEN_FRESNEL_BIAS,
      power: EDGE_DARKEN_FRESNEL_POWER,
      leftColor: new Color3(EDGE_DARKEN_AMOUNT, EDGE_DARKEN_AMOUNT, EDGE_DARKEN_AMOUNT),
      rightColor: Color3.White(),
    });

    // Applies equally to a model-backed node: ModelLibrary discards a template's own imported
    // material after merge (ModelLibrary.ts, buildTemplate's own closing comment) unless a
    // caller opts into `preserveMaterials` — which NodeMeshFactory never does; only
    // AvatarManager's avatar parts do, a different code path entirely. So every node this class
    // builds, model-backed or procedural, gets exactly this material, rim and all.
    //
    // Some silhouettes read by treatment rather than shape — `external` is a ghosted box. The
    // resting alpha is recorded in metadata by create(), so being held still fades it further
    // rather than snapping it back to solid.
    material.alpha = restingAlphaFor(silhouette);
    mesh.material = material;

    return mesh;
  }

  /**
   * An explicit style colour wins; otherwise the catalogue's default for the type.
   *
   * Which means a `database` is green because it is a database, without every caller having to
   * remember to set that — and recolouring one stays possible.
   */
  private colorFor(node: NodeDto): string {
    return node.style.color ?? this.typeOf(node)?.defaultColor ?? DEFAULT_SHAPE_COLOR;
  }
}

/** Shared by the connection renderer, which also has to orient geometry between points. */
export function orientBetween(mesh: AbstractMesh, from: Vector3, to: Vector3): void {
  const direction = to.subtract(from);
  const length = direction.length();

  mesh.position.copyFrom(from.add(to).scale(0.5));
  mesh.scaling.y = Math.max(length, 0.0001);

  if (length < 0.0001) {
    return;
  }

  const unit = direction.scale(1 / length);
  const up = Vector3.Up();
  const dot = Vector3.Dot(up, unit);

  (mesh.rotationQuaternion ??= Quaternion.Identity());

  // Cross product is degenerate when the two nodes are stacked vertically, so handle the
  // parallel and antiparallel cases explicitly rather than normalising a zero vector.
  if (dot > 0.99999) {
    mesh.rotationQuaternion.copyFrom(Quaternion.Identity());
    return;
  }

  if (dot < -0.99999) {
    mesh.rotationQuaternion.copyFrom(Quaternion.RotationAxis(Vector3.Right(), Math.PI));
    return;
  }

  const axis = Vector3.Cross(up, unit).normalize();
  mesh.rotationQuaternion.copyFrom(Quaternion.RotationAxis(axis, Math.acos(dot)));
}
